// src/components/BuildPanel.jsx
import React, { useState, useEffect } from 'react';
import { fetchBuildData } from '../services/buildDataService';
import { fetchBylawData } from '../services/bylawDataService';
import { computeBuiltUp, parseMeters } from '../utils/builtUpCalc';

const SOIL_TYPES     = ['Sandy', 'Clay', 'Loamy', 'Rocky', 'Black Cotton', 'Alluvial'];
const CLIMATE_TYPES  = ['Hot & Dry', 'Warm & Humid', 'Composite', 'Cold & Sunny', 'Temperate'];
const BUILDING_TYPES = ['Residential', 'Commercial', 'Mixed Use', 'Industrial', 'Institutional'];

const FLOOR_HEIGHT_M = 3;

function SectionTitle({ children }) {
  return <div className="build-section-title">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div className="build-field">
      <label className="build-label">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input className="build-input" type="text" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );
}

function NumberInput({ value, onChange, placeholder, min, step }) {
  return (
    <input className="build-input" type="number" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder} min={min} step={step} />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select className="build-input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">- select -</option>
      {options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
    </select>
  );
}

function BuildPanel({ project, onUpdate }) {
  // Deterministic OSM road detection (Site3DView emits it when context loads).
  // A SUGGESTION only: road width drives the setback band, so it is never
  // auto-applied — a plausible wrong value nobody re-checks is worse than a
  // blank field. See utils/roadDetect.js.
  const [roadHint, setRoadHint] = useState(null);
  useEffect(() => {
    const onDetected = (e) => setRoadHint(e.detail?.best || null);
    window.addEventListener('roadEdgesDetected', onDetected);
    return () => window.removeEventListener('roadEdgesDetected', onDetected);
  }, []);
  const [siteData, setSiteData] = useState({
    siteSetback:     project?.siteData?.siteSetback      ?? project?.buildData?.siteSetback ?? '',
    // Road WIDTH (the carriageway the plot abuts) — distinct from road MARGIN
    // (the setback from it). DCR setback/FSI/height tables band on the WIDTH,
    // so without this the road-width-banded rules cannot resolve at all.
    roadWidth:       project?.siteData?.roadWidth        ?? project?.buildData?.roadWidth ?? '',
    roadMargin:      project?.buildData?.roadMargin      ?? '',
    heightLimit:     project?.buildData?.heightLimit     ?? '',
    fsi:             project?.buildData?.fsi             ?? '',
    soilType:        project?.buildData?.soilType        ?? project?.soilType ?? '',
    climateType:     project?.buildData?.climateType     ?? project?.climate  ?? '',
    siteBuiltUpArea: project?.buildData?.siteBuiltUpArea ?? '',
    siteConstrCost:  project?.buildData?.siteConstrCost  ?? '',
  });

  const [buildingInfo, setBuildingInfo] = useState({
    buildingType:   project?.buildData?.buildingType   ?? '',
    groundCoverage: project?.buildData?.groundCoverage ?? '',
    builtUpArea:    project?.buildData?.builtUpArea    ?? '',
    constrCost:     project?.buildData?.constrCost     ?? '',
    floorNo:        project?.buildData?.floorNo        ?? '',
  });

  const [autoFill, setAutoFill] = useState({
    loading: false, errors: [], done: false,
    extra: null, bylaw: null, builtUp: null,
    gcPct: null, // ground coverage % from the last bylaw fetch (Delhi)
  });

  // "12 m" / "12" -> 12 ; anything unparseable -> null
  const parseNum = (raw) => {
    const v = parseFloat(String(raw ?? '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(v) && v > 0 ? v : null;
  };

  const commit = (nextSite, nextBuilding) => {
    setSiteData(nextSite);
    setBuildingInfo(nextBuilding);
    onUpdate?.({
      ...project,
      siteData: nextSite,
      // Site3DView reads project.roadWidthM directly when emitting compliance
      // facts, so mirror the parsed value to the top level.
      roadWidthM: parseNum(nextSite.roadWidth),
      buildData: { ...(project?.buildData || {}), ...nextSite, ...nextBuilding },
    });
  };

  const updateSite = (key, value) => commit({ ...siteData, [key]: value }, buildingInfo);
  const updateBuilding = (key, value) => commit(siteData, { ...buildingInfo, [key]: value });

  // derive built-up from a site-data object + optional ground-coverage %
  const deriveBuiltUpInto = (sourceSite, baseSite, baseBuilding, gcPct) => {
    const bu = computeBuiltUp(project, {
      setbackM:          parseMeters(sourceSite.siteSetback),
      fsi:               parseFloat(sourceSite.fsi) || 0,
      heightLimitM:      parseMeters(sourceSite.heightLimit),
      floorHeightM:      FLOOR_HEIGHT_M,
      groundCoveragePct: gcPct ?? null,
    });

    const nextSite = { ...baseSite };
    const nextBuilding = { ...baseBuilding };

    if (bu.builtUpM2 != null) {
      nextSite.siteBuiltUpArea = Math.round(bu.builtUpM2);
      nextBuilding.builtUpArea = Math.round(bu.builtUpM2);
    }
    if (bu.floors != null) nextBuilding.floorNo = bu.floors;
    if (bu.footprintM2 != null) nextBuilding.groundCoverage = Math.round(bu.footprintM2);

    return { bu, nextSite, nextBuilding };
  };

  const handleAutoFill = async () => {
    const lat = project?.centerLat;
    const lng = project?.centerLng;

    if (lat == null || lng == null) {
      setAutoFill((s) => ({ ...s, loading: false, errors: ['No coordinates found on this project.'], done: false }));
      return;
    }

    setAutoFill({ loading: true, errors: [], done: false, extra: null, bylaw: null, builtUp: null, gcPct: null });

    const [envRes, bylawRes] = await Promise.allSettled([
      fetchBuildData(lat, lng),
      fetchBylawData(lat, lng, {
        plotAreaM2: project?.areaM2,
        useType: buildingInfo.buildingType || undefined,
        // road-width-banded setback/FSI tables can only resolve if we send it
        roadWidthM: parseNum(siteData.roadWidth) ?? undefined,
      }),
    ]);

    let nextSite = { ...siteData };
    let extra = null;
    let bylawInfo = null;
    let gcPct = null;
    const errors = [];

    if (envRes.status === 'fulfilled') {
      const f = envRes.value || {};
      if (f.climateType) nextSite.climateType = f.climateType;
      if (f.soilType) nextSite.soilType = f.soilType;
      (f.errors || []).forEach((e) => errors.push(e));
      extra = { elevationM: f.elevationM, floodRisk: f.floodRisk };
    } else {
      errors.push(`Environment: ${envRes.reason?.message || envRes.reason}`);
    }

    if (bylawRes.status === 'fulfilled' && bylawRes.value?.ok) {
      const b = bylawRes.value;
      bylawInfo = b;
      const v = b.values || {};
      if (v.siteSetback != null) nextSite.siteSetback = `${v.siteSetback} m`;
      if (v.roadMargin != null)  nextSite.roadMargin  = `${v.roadMargin} m`;
      if (v.heightLimit != null) nextSite.heightLimit = `${v.heightLimit} m`;
      if (v.fsi != null)         nextSite.fsi         = String(v.fsi);
      if (Number.isFinite(b.groundCoveragePct)) gcPct = b.groundCoveragePct;
    } else {
      const msg = bylawRes.status === 'fulfilled'
        ? bylawRes.value?.error || 'no bylaw data returned'
        : bylawRes.reason?.message || bylawRes.reason;
      errors.push(`Bylaws: ${msg}`);
    }

    const { bu, nextSite: sWithBU, nextBuilding } =
      deriveBuiltUpInto(nextSite, nextSite, buildingInfo, gcPct);

    commit(sWithBU, nextBuilding);
    setAutoFill({ loading: false, errors, done: true, extra, bylaw: bylawInfo, builtUp: bu, gcPct });
  };

  const handleComputeBuiltUp = () => {
    const { bu, nextSite, nextBuilding } =
      deriveBuiltUpInto(siteData, siteData, buildingInfo, autoFill.gcPct);
    commit(nextSite, nextBuilding);
    setAutoFill((s) => ({ ...s, builtUp: bu }));
  };

  const bu = autoFill.builtUp;
  const fmt = (n) => (n == null ? '-' : Math.round(n).toLocaleString());

  return (
    <div className="build-panel">

      <div className="build-autofill-row">
        <button type="button" className="btn build-autofill-btn" onClick={handleAutoFill}
          disabled={autoFill.loading}
          title="Auto-fill Climate & Soil + Bylaws (Setback, Road Margin, Height, FSI) from project coordinates">
          {autoFill.loading ? 'Fetching...' : 'Auto-fill from Location'}
        </button>

        <button type="button" className="btn build-autofill-btn" onClick={handleComputeBuiltUp}
          disabled={autoFill.loading}
          title="Recompute Built-up Area from the current Setback / Height / FSI values"
          style={{ marginLeft: 6 }}>
          Compute Built-up
        </button>

        {autoFill.done && autoFill.errors.length === 0 && (
          <span className="build-autofill-success small">
            Filled
            {autoFill.extra?.elevationM != null ? ` - ${autoFill.extra.elevationM} m elev.` : ''}
            {autoFill.extra?.floodRisk ? ` - Flood: ${autoFill.extra.floodRisk}` : ''}
          </span>
        )}

        {autoFill.errors.length > 0 && (
          <div className="build-autofill-errors small muted">
            {autoFill.errors.map((e, i) => (<div key={i}>! {e}</div>))}
          </div>
        )}
      </div>

      {autoFill.bylaw?.ok && (
        <div className="small muted" style={{ margin: '6px 0' }}>
          {autoFill.bylaw.jurisdiction?.state && (<div>Jurisdiction: {autoFill.bylaw.jurisdiction.state}</div>)}
          {autoFill.bylaw.regulationName && (<div>Regulation: {autoFill.bylaw.regulationName}</div>)}
          {(autoFill.bylaw.assumptions || []).length > 0 && (
            <div style={{ marginTop: 2 }}>
              Assumptions:
              {autoFill.bylaw.assumptions.map((a, i) => (<div key={i}>- {a}</div>))}
            </div>
          )}
          <div style={{ marginTop: 2, fontStyle: 'italic' }}>
            Auto-filled values are estimates - verify against the official regulation before use.
          </div>
        </div>
      )}

      <SectionTitle>Site Data</SectionTitle>

      <Field label="Site Setback">
        <TextInput value={siteData.siteSetback} onChange={(v) => updateSite('siteSetback', v)} placeholder="e.g. 3 m" />
      </Field>
      <Field label="Road Width">
        <TextInput value={siteData.roadWidth} onChange={(v) => updateSite('roadWidth', v)} placeholder="e.g. 12 m" />
        {roadHint?.road?.widthM != null && !siteData.roadWidth && (
          <div style={{ fontSize: 10.5, color: 'var(--text-dim,#9aa7c0)', marginTop: 4, lineHeight: 1.45 }}>
            Detected {roadHint.road.cls}
            {roadHint.road.name ? ` (${roadHint.road.name})` : ''} {roadHint.distanceM} m away.
            <button
              type="button"
              onClick={() => updateSite('roadWidth', `${roadHint.road.widthM} m`)}
              style={{
                marginLeft: 5, fontSize: 10.5, padding: '1px 7px', borderRadius: 6,
                border: '1px solid var(--accent,#48b0f7)', background: 'transparent',
                color: 'var(--accent,#48b0f7)', cursor: 'pointer',
              }}
            >
              use {roadHint.road.widthM} m
            </button>
            <div style={{ color: 'var(--warn)', marginTop: 3 }}>
              {roadHint.road.basis} — carriageway only; a DCR road width usually
              includes footpaths. Verify before relying on it.
            </div>
          </div>
        )}
      </Field>
      <Field label="Road Margin">
        <TextInput value={siteData.roadMargin} onChange={(v) => updateSite('roadMargin', v)} placeholder="e.g. 6 m" />
      </Field>
      <Field label="Height Limit">
        <TextInput value={siteData.heightLimit} onChange={(v) => updateSite('heightLimit', v)} placeholder="e.g. 15 m" />
      </Field>
      <Field label="FSI">
        <NumberInput value={siteData.fsi} onChange={(v) => updateSite('fsi', v)} placeholder="e.g. 1.5" step="0.1" />
      </Field>
      <Field label="Soil Type">
        <SelectInput value={siteData.soilType} onChange={(v) => updateSite('soilType', v)} options={SOIL_TYPES} />
      </Field>
      <Field label="Climate Type">
        <SelectInput value={siteData.climateType} onChange={(v) => updateSite('climateType', v)} options={CLIMATE_TYPES} />
      </Field>
      <Field label="Built Up Area (m2)">
        <NumberInput value={siteData.siteBuiltUpArea} onChange={(v) => updateSite('siteBuiltUpArea', v)} placeholder="e.g. 200" />
      </Field>
      <Field label="Construction Cost (Rs)">
        <NumberInput value={siteData.siteConstrCost} onChange={(v) => updateSite('siteConstrCost', v)} placeholder="e.g. 5000000" />
      </Field>

      {bu && (
        <div className="small muted" style={{ margin: '4px 0 8px' }}>
          <div>Gross plot area: {fmt(bu.grossAreaM2)} m2</div>
          <div>
            Footprint{bu.footprintBasis === 'ground_coverage'
              ? ` (${bu.groundCoveragePct}% ground coverage)`
              : ` (after ${bu.setbackApplied || 0} m setback)`}: {fmt(bu.footprintM2)} m2
          </div>
          <div>Floors (height / {bu.floorHeightM} m): {bu.floors ?? '-'}</div>
          <div>Massing cap (footprint x floors): {bu.massingCapM2 ? fmt(bu.massingCapM2) + ' m2' : 'n/a'}</div>
          <div>FSI cap (gross x FSI): {bu.fsiCapM2 == null ? '-' : `${fmt(bu.fsiCapM2)} m2`}</div>
          <div>
            <strong>Built-up = {fmt(bu.builtUpM2)} m2</strong>
            {bu.bindingConstraint && bu.bindingConstraint !== 'none'
              ? ` (limited by ${bu.bindingConstraint === 'fsi' ? 'FSI' : 'massing/height'})`
              : ''}
          </div>
          {bu.setbackCollapsed && (
            <div style={{ color: 'var(--err)' }}>! Setback collapses the plot - no buildable footprint.</div>
          )}
        </div>
      )}

      <SectionTitle>Building Info</SectionTitle>

      <Field label="Building Type">
        <SelectInput value={buildingInfo.buildingType} onChange={(v) => updateBuilding('buildingType', v)} options={BUILDING_TYPES} />
      </Field>
      <Field label="Ground Coverage Area (m2)">
        <NumberInput value={buildingInfo.groundCoverage} onChange={(v) => updateBuilding('groundCoverage', v)} placeholder="e.g. 150" />
      </Field>
      <Field label="Built Up Area (m2)">
        <NumberInput value={buildingInfo.builtUpArea} onChange={(v) => updateBuilding('builtUpArea', v)} placeholder="e.g. 600" />
      </Field>
      <Field label="Construction Cost (Rs)">
        <NumberInput value={buildingInfo.constrCost} onChange={(v) => updateBuilding('constrCost', v)} placeholder="e.g. 8000000" />
      </Field>
      <Field label="Floor No.">
        <NumberInput value={buildingInfo.floorNo} onChange={(v) => updateBuilding('floorNo', v)} placeholder="e.g. 4" min="0" />
      </Field>

    </div>
  );
}

export default BuildPanel;
